import * as React from "react";

export type MarginsInches = { top: number; bottom: number; left: number; right: number };

export type PaperSize = {
  name: string;
  width: number;   // inches
  height: number;  // inches
};

export type PageSetupConfig = {
  margins: MarginsInches;
  paperSize: PaperSize;
  orientation: 'portrait' | 'landscape';
  pageColor: string;
  // Word spacing adjustment in percent. 0 = normal (100%).
  // Negative values tighten spaces, positive values loosen.
  // Range suggestion: -20 to +50.
  wordSpacingPercent?: number;
};

export const PAPER_SIZES: PaperSize[] = [
  { name: "Letter (8.5\" x 11\")", width: 8.5, height: 11 },
  { name: "Tabloid (11\" x 17\")", width: 11, height: 17 },
  { name: "Legal (8.5\" x 14\")", width: 8.5, height: 14 },
  { name: "Statement (5.5\" x 8.5\")", width: 5.5, height: 8.5 },
  { name: "Executive (7.25\" x 10.5\")", width: 7.25, height: 10.5 },
  { name: "Folio (8.5\" x 13\")", width: 8.5, height: 13 },
  { name: "A3 (11.69\" x 16.54\")", width: 11.69, height: 16.54 },
  { name: "A4 (8.27\" x 11.69\")", width: 8.27, height: 11.69 },
  { name: "A5 (5.83\" x 8.27\")", width: 5.83, height: 8.27 },
  { name: "B4 (9.84\" x 13.90\")", width: 9.84, height: 13.90 },
  { name: "B5 (6.93\" x 9.84\")", width: 6.93, height: 9.84 },
];

export const DEFAULT_PAGE_SETUP: PageSetupConfig = {
  margins: { top: 1, bottom: 1, left: 1, right: 1 },
  paperSize: PAPER_SIZES[0], // Letter
  orientation: 'portrait',
  pageColor: '#ffffff',
  wordSpacingPercent: 0
};

export default function PageSetupDialog({
  open,
  initial,
  onApply,
  onClose,
}: {
  open: boolean;
  initial: PageSetupConfig;
  onApply: (config: PageSetupConfig) => void;
  onClose: () => void;
}) {
  // Delayed unmount so close can animate
  const [shouldRender, setShouldRender] = React.useState(open);
  React.useEffect(() => { if (open) setShouldRender(true); }, [open]);
  const handleAnimEnd = () => { if (!open) setShouldRender(false); };

  const [activeTab, setActiveTab] = React.useState<'pages' | 'pageless' | 'advanced'>('pages');
  const [margins, setMargins] = React.useState<MarginsInches>(initial.margins);
  const [orientation, setOrientation] = React.useState<'portrait' | 'landscape'>(initial.orientation);
  const [paperSize, setPaperSize] = React.useState<PaperSize>(initial.paperSize);
  const [pageColor, setPageColor] = React.useState(initial.pageColor);
  const [backgroundColor, setBackgroundColor] = React.useState('#ffffff');
  const [wordSpacingPercent, setWordSpacingPercent] = React.useState<number>(initial.wordSpacingPercent ?? 0);

  React.useEffect(() => { 
    if (open) {
      setMargins(initial.margins);
      setOrientation(initial.orientation);
      setPaperSize(initial.paperSize);
      setPageColor(initial.pageColor);
      setWordSpacingPercent(initial.wordSpacingPercent ?? 0);
      setActiveTab('pages');
    }
  }, [open, initial]);

  if (!shouldRender) return null;

  const handleApply = () => {
    // Pass complete PageSetupConfig to parent
    onApply({
      margins,
      paperSize,
      orientation,
      pageColor,
      wordSpacingPercent
    });
    onClose();
  };

  const setMargin = (key: keyof MarginsInches) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setMargins((prev) => ({ ...prev, [key]: clamp(parseFloat(e.target.value)) }));

  return (
    <div className="fixed inset-0 z-[1000]">
      {/* backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 ${open ? 'edg-overlay-in' : 'edg-overlay-out'}`}
        onClick={onClose}
        onAnimationEnd={handleAnimEnd}
      />
      
      {/* dialog */}
      <div
        className={`absolute left-1/2 top-1/2 w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl ${open ? 'edg-content-in' : 'edg-content-out'}`}
        onAnimationEnd={handleAnimEnd}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="mb-6 text-xl font-semibold text-gray-800 dark:text-white">Page setup</h2>
        
        {/* Tabs */}
        <div className="mb-6 flex border-b border-gray-200 dark:border-slate-600">
          <button
            className={`pb-2 px-1 text-sm font-medium ${
              activeTab === 'pages' 
                ? 'text-lex-accent border-b-2 border-lex-accent' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('pages')}
          >
            Pages
          </button>
          <button
            className={`pb-2 px-1 ml-6 text-sm font-medium ${
              activeTab === 'pageless' 
                ? 'text-lex-accent border-b-2 border-lex-accent' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('pageless')}
          >
            Pageless
          </button>
          <button
            className={`pb-2 px-1 ml-6 text-sm font-medium ${
              activeTab === 'advanced' 
                ? 'text-lex-accent border-b-2 border-lex-accent' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('advanced')}
          >
            Advanced
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'pages' ? (
            <PagesTabContent
              margins={margins}
              setMargin={setMargin}
              orientation={orientation}
              setOrientation={setOrientation}
              paperSize={paperSize}
              setPaperSize={setPaperSize}
              pageColor={pageColor}
              setPageColor={setPageColor}
              wordSpacingPercent={wordSpacingPercent}
              setWordSpacingPercent={setWordSpacingPercent}
            />
          ) : activeTab === 'pageless' ? (
            <PagelessTabContent
              backgroundColor={backgroundColor}
              setBackgroundColor={setBackgroundColor}
            />
          ) : (
            <AdvancedTabContent
              pageSetup={{ margins, paperSize, orientation, pageColor, wordSpacingPercent }}
            />
          )}
        </div>

        {/* Action buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            className="text-sm text-lex-accent hover:text-lex-accent-hover"
            onClick={() => {
              // Set default margins
              setMargins({ top: 1, bottom: 1, left: 1, right: 1 });
            }}
          >
            Set as default
          </button>
          <div className="space-x-3">
            <button
              className="px-4 py-2 text-sm text-lex-accent hover:text-lex-accent-hover"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded hover:bg-lex-accent-hover"
              onClick={handleApply}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PagesTabContent({
  margins,
  setMargin,
  orientation,
  setOrientation,
  paperSize,
  setPaperSize,
  pageColor,
  setPageColor,
  wordSpacingPercent,
  setWordSpacingPercent,
}: {
  margins: MarginsInches;
  setMargin: (key: keyof MarginsInches) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  orientation: 'portrait' | 'landscape';
  setOrientation: (orientation: 'portrait' | 'landscape') => void;
  paperSize: PaperSize;
  setPaperSize: (size: PaperSize) => void;
  pageColor: string;
  setPageColor: (color: string) => void;
  wordSpacingPercent: number;
  setWordSpacingPercent: (n: number) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Apply to */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Apply to</label>
        <select className="w-full h-9 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm text-gray-900 dark:text-white">
          <option>Whole document</option>
          <option>This section</option>
          <option>This page forward</option>
        </select>
      </div>

      {/* Orientation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Orientation</label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="orientation"
              value="portrait"
              checked={orientation === 'portrait'}
              onChange={(e) => setOrientation(e.target.value as 'portrait')}
              className="mr-2"
            />
            <span className="text-sm text-gray-900 dark:text-white">Portrait</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="orientation"
              value="landscape"
              checked={orientation === 'landscape'}
              onChange={(e) => setOrientation(e.target.value as 'landscape')}
              className="mr-2"
            />
            <span className="text-sm text-gray-900 dark:text-white">Landscape</span>
          </label>
        </div>
      </div>

      {/* Paper size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Paper size</label>
        <select 
          className="w-full h-9 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm text-gray-900 dark:text-white"
          value={paperSize.name}
          onChange={(e) => {
            const selected = PAPER_SIZES.find(size => size.name === e.target.value);
            if (selected) setPaperSize(selected);
          }}
        >
          {PAPER_SIZES.map((size) => (
            <option key={size.name} value={size.name}>
              {size.name}
            </option>
          ))}
        </select>
      </div>

      {/* Page color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Page color</label>
        <div className="flex items-center space-x-2">
          <div 
            className="w-8 h-8 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
            style={{ backgroundColor: pageColor }}
            onClick={() => {
              // Simple color picker - in real implementation, use a proper color picker
              const colors = ['#ffffff', '#f0f0f0', '#e0e0e0', '#d0d0d0'];
              const currentIndex = colors.indexOf(pageColor);
              const nextIndex = (currentIndex + 1) % colors.length;
              setPageColor(colors[nextIndex]);
            }}
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">Click to cycle colors</span>
        </div>
      </div>

      {/* Word spacing */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Word spacing</label>
        <div className="grid grid-cols-2 gap-3 items-center">
          <input
            type="range"
            min={-20}
            max={50}
            step={1}
            value={Number.isFinite(wordSpacingPercent) ? wordSpacingPercent : 0}
            onChange={(e) => setWordSpacingPercent(Number(e.target.value))}
          />
          <input
            type="number"
            className="w-full h-8 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white"
            min={-20}
            max={50}
            step={1}
            value={Number.isFinite(wordSpacingPercent) ? wordSpacingPercent : 0}
            onChange={(e) => setWordSpacingPercent(Number(e.target.value))}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Percent relative to normal spaces (0 = default). Negative tightens, positive loosens.</p>
      </div>

      {/* Margins */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Margins (inches)</label>
        <div className="grid grid-cols-4 gap-3">
          <MarginField label="Top" value={margins.top} onChange={setMargin("top")} />
          <MarginField label="Bottom" value={margins.bottom} onChange={setMargin("bottom")} />
          <MarginField label="Left" value={margins.left} onChange={setMargin("left")} />
          <MarginField label="Right" value={margins.right} onChange={setMargin("right")} />
        </div>
      </div>
    </div>
  );
}

function PagelessTabContent({
  backgroundColor,
  setBackgroundColor,
}: {
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Illustrative graphic */}
      <div className="flex justify-center">
        <div className="w-64 h-32 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded p-4">
          <div className="space-y-2">
            <div className="h-2 bg-gray-300 dark:bg-slate-500 rounded"></div>
            <div className="h-2 bg-gray-300 dark:bg-slate-500 rounded w-3/4"></div>
            <div className="h-2 bg-gray-300 dark:bg-slate-500 rounded w-1/2"></div>
            <div className="grid grid-cols-4 gap-1 mt-2">
              <div className="h-4 bg-yellow-200 dark:bg-yellow-600 rounded"></div>
              <div className="h-4 bg-lex-accent-soft rounded"></div>
              <div className="h-4 bg-green-200 dark:bg-green-600 rounded"></div>
              <div className="h-4 bg-red-200 dark:bg-red-600 rounded"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        <p className="mb-2">
          The pageless format allows you to add wide images and tables, and consume content without the interruption of page breaks.
        </p>
        <button className="text-lex-accent hover:text-lex-accent-hover underline">
          Learn more
        </button>
      </div>

      {/* Background color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Background color</label>
        <div className="flex items-center space-x-2">
          <div 
            className="w-8 h-8 rounded border border-gray-300 dark:border-slate-600 cursor-pointer"
            style={{ backgroundColor: backgroundColor }}
            onClick={() => {
              // Simple color picker - in real implementation, use a proper color picker
              const colors = ['#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6'];
              const currentIndex = colors.indexOf(backgroundColor);
              const nextIndex = (currentIndex + 1) % colors.length;
              setBackgroundColor(colors[nextIndex]);
            }}
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">Click to cycle colors</span>
        </div>
      </div>
    </div>
  );
}

function AdvancedTabContent({
  pageSetup,
}: {
  pageSetup: PageSetupConfig;
}) {
  // Compute simple, low-cost browser metrics
  const fontCss = {
    family: (typeof getComputedStyle === 'function') ? getComputedStyle(document.documentElement).getPropertyValue('--font-document') : '',
  };

  const activeFamily = (fontCss.family || '').replace(/["']/g, '').split(',')[0].trim() || 'Inter';

  // Measure baseline space via canvas (approximation of browser rendering)
  const [baselineSpacePx, setBaselineSpacePx] = React.useState<number | null>(null);
  React.useEffect(() => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const sizePx =  (pageSetup as any)?.fontSizePx ||  (document.body ? parseFloat(getComputedStyle(document.body).fontSize) || 16 : 16);
      const weight = 400;
      const style = 'normal';
      ctx.font = `${style} ${weight} ${sizePx}px ${activeFamily}`;
      const w = ctx.measureText(' ').width;
      setBaselineSpacePx(w);
    } catch {}
  }, [activeFamily, pageSetup]);

  // Show current word spacing scale
  const scalePercent = (pageSetup.wordSpacingPercent ?? 0);

  // Overlay toggles (dispatch to app for listeners to consume)
  // Initialize from persisted settings if available (localStorage survives page refresh)
  const OVERLAY_STORAGE_KEY = 'edgedocs_overlay_settings';
  
  const getPersistedOverlaySetting = (key: string, defaultValue: boolean = false): boolean => {
    try {
      // Try localStorage first (survives refresh)
      const stored = localStorage.getItem(OVERLAY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.[key] ?? defaultValue;
      }
      // Fallback to window (for backwards compatibility)
      const persisted = (window as any).__edg_overlaySettings;
      return persisted?.[key] ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [showCaretMap, setShowCaretMap] = React.useState(() => getPersistedOverlaySetting('showCaretMap'));
  const [showGlyphBoxes, setShowGlyphBoxes] = React.useState(() => getPersistedOverlaySetting('showGlyphBoxes'));
  const [showLineBoxes, setShowLineBoxes] = React.useState(() => getPersistedOverlaySetting('showLineBoxes'));
  const [showBaselines, setShowBaselines] = React.useState(() => getPersistedOverlaySetting('showBaselines'));
  const [showWhitespaceBadges, setShowWhitespaceBadges] = React.useState(() => getPersistedOverlaySetting('showWhitespaceBadges'));
  const [showBreakMarkers, setShowBreakMarkers] = React.useState(() => getPersistedOverlaySetting('showBreakMarkers'));
  const [showDebugDashboard, setShowDebugDashboard] = React.useState(() => getPersistedOverlaySetting('showDebugDashboard'));
  
  // Incremental layout testing state
  const [showIncrementalLogs, setShowIncrementalLogs] = React.useState(() => getPersistedOverlaySetting('showIncrementalLogs'));
  const [showDeltaDetails, setShowDeltaDetails] = React.useState(() => getPersistedOverlaySetting('showDeltaDetails'));
  const [showSnapshotDiff, setShowSnapshotDiff] = React.useState(() => getPersistedOverlaySetting('showSnapshotDiff'));
  const [highlightAffectedParagraphs, setHighlightAffectedParagraphs] = React.useState(() => getPersistedOverlaySetting('highlightAffectedParagraphs'));
  const [lastTestResult, setLastTestResult] = React.useState<{timestamp: string, passed: boolean, message: string, data?: any} | null>(null);

  React.useEffect(() => {
    try {
      const detail = { 
        showCaretMap, showGlyphBoxes, showLineBoxes, showBaselines, showWhitespaceBadges, showBreakMarkers, showDebugDashboard,
        showIncrementalLogs, showDeltaDetails, showSnapshotDiff, highlightAffectedParagraphs
      };
      // Persist to localStorage (survives page refresh)
      localStorage.setItem(OVERLAY_STORAGE_KEY, JSON.stringify(detail));
      // Also set on window for backwards compatibility
      (window as any).__edg_overlaySettings = detail;
      // Dispatch event to canvas
      window.dispatchEvent(new CustomEvent('edgedocs:overlay-settings', { detail }));
    } catch {}
  }, [showCaretMap, showGlyphBoxes, showLineBoxes, showBaselines, showWhitespaceBadges, showBreakMarkers, showDebugDashboard, showIncrementalLogs, showDeltaDetails, showSnapshotDiff, highlightAffectedParagraphs]);
  
  // Listen for test results
  React.useEffect(() => {
    const handleTestResult = (e: any) => {
      setLastTestResult(e.detail);
    };
    window.addEventListener('edgedocs:test-result', handleTestResult);
    return () => window.removeEventListener('edgedocs:test-result', handleTestResult);
  }, []);

  // Listen for delta calculation events (Phase 2)
  const [lastDelta, setLastDelta] = React.useState<any>(null);
  React.useEffect(() => {
    const handleDelta = (e: any) => {
      setLastDelta(e.detail);
    };
    window.addEventListener('edgedocs:delta-calculated', handleDelta);
    return () => window.removeEventListener('edgedocs:delta-calculated', handleDelta);
  }, []);

  // Listen for snapshot diff events (Phase 3)
  const [snapshotDiff, setSnapshotDiff] = React.useState<any>(null);
  React.useEffect(() => {
    const handleDiff = (e: any) => {
      setSnapshotDiff(e.detail);
    };
    window.addEventListener('edgedocs:snapshot-diff', handleDiff);
    return () => window.removeEventListener('edgedocs:snapshot-diff', handleDiff);
  }, []);

  const copyDiagnostics = async () => {
    const diagnostic = {
      timestamp: new Date().toISOString(),
      activeFamily,
      baselineSpacePx,
      wordSpacingPercent: scalePercent,
      pageSetup,
      overlays: { showCaretMap, showGlyphBoxes, showLineBoxes },
    };
    const json = JSON.stringify(diagnostic, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      alert('Diagnostics copied to clipboard');
    } catch {
      // Fallback: prompt if clipboard not available
      window.prompt('Copy diagnostics JSON:', json);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">Engine</h3>
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
          <div>WASM: client-loaded (hash shown in console logs)</div>
          <div>Typesetter: HB/Unicode versions available in snapshot (when enabled)</div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">Fonts</h3>
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
          <div>Active family: {activeFamily || 'unknown'}</div>
          <div>Baseline space (browser): {baselineSpacePx != null ? `${baselineSpacePx.toFixed(2)} px` : 'measuring…'}</div>
          <div>Word spacing delta: {scalePercent > 0 ? `+${scalePercent}%` : `${scalePercent}%`}</div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">Pagination</h3>
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
          <div>Orientation: {pageSetup.orientation}</div>
          <div>Paper: {pageSetup.paperSize.name}</div>
          <div>Margins (in): T {pageSetup.margins.top}, R {pageSetup.margins.right}, B {pageSetup.margins.bottom}, L {pageSetup.margins.left}</div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-slate-600 pt-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">Overlays</h3>
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2">
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showDebugDashboard} onChange={(e) => setShowDebugDashboard(e.target.checked)} />
            <span className="font-semibold">Show debug dashboard (snapshot staleness)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showCaretMap} onChange={(e) => setShowCaretMap(e.target.checked)} />
            <span>Show caret map</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showGlyphBoxes} onChange={(e) => setShowGlyphBoxes(e.target.checked)} />
            <span>Show glyph boxes</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showLineBoxes} onChange={(e) => setShowLineBoxes(e.target.checked)} />
            <span>Show line boxes</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showBaselines} onChange={(e) => setShowBaselines(e.target.checked)} />
            <span>Show baselines (baseline/ascent/descent)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showWhitespaceBadges} onChange={(e) => setShowWhitespaceBadges(e.target.checked)} />
            <span>Show whitespace badges (sp/nbsp/tab/zwsp/shy)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={showBreakMarkers} onChange={(e) => setShowBreakMarkers(e.target.checked)} />
            <span>Show break markers</span>
          </label>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-slate-600 pt-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">Export diagnostics</h3>
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2">
          <button
            onClick={copyDiagnostics}
            className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-700 text-gray-800 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-slate-600"
          >
            Copy diagnostics JSON
          </button>
          <div>Snapshot/trace download can be added once worker exposes an endpoint.</div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-slate-600 pt-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">
          🧪 Incremental Layout Testing
        </h3>
        
        {/* Test Controls */}
        <div className="space-y-2 mb-4">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('edgedocs:test-incremental-layout'))}
            className="w-full px-3 py-2 rounded bg-lex-accent-soft text-lex-accent hover:bg-lex-state-hover text-sm font-medium"
          >
            🧪 Run Full Incremental Layout Test
          </button>
          
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('edgedocs:test-apply-delta'))}
            className="w-full px-3 py-2 rounded bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 hover:bg-green-200 dark:hover:bg-green-800 text-sm"
          >
            Test applyEditDelta
          </button>
          
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('edgedocs:test-layout-affected'))}
            className="w-full px-3 py-2 rounded bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-100 hover:bg-purple-200 dark:hover:bg-purple-800 text-sm"
          >
            Test layoutAffectedParagraphs
          </button>
          
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('edgedocs:test-spacebar-bug'))}
            className="w-full px-3 py-2 rounded bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 hover:bg-red-200 dark:hover:bg-red-800 text-sm"
          >
            🐛 Test Spacebar Bug (Paste + Space)
          </button>
        </div>
        
        {/* Live Monitoring Toggles */}
        <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2 mb-4">
          <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Live Monitoring:</div>
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              checked={showIncrementalLogs} 
              onChange={(e) => setShowIncrementalLogs(e.target.checked)} 
            />
            <span>Log all incremental layout operations</span>
          </label>
          
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              checked={showDeltaDetails} 
              onChange={(e) => setShowDeltaDetails(e.target.checked)} 
            />
            <span>Show delta calculation details</span>
          </label>
          
          {/* Delta Details Display (Phase 2) */}
          {lastDelta && showDeltaDetails && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-200 dark:border-slate-600">
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Last Delta:</div>
              <pre className="mt-1 text-xs overflow-auto max-h-32 text-gray-600 dark:text-gray-400 font-mono">
                {JSON.stringify(lastDelta, null, 2)}
              </pre>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                Position: {lastDelta.position} | Delete: {lastDelta.deleteCount} | Insert: {lastDelta.insertText.length} chars
              </div>
            </div>
          )}
          
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              checked={showSnapshotDiff} 
              onChange={(e) => setShowSnapshotDiff(e.target.checked)} 
            />
            <span>Show snapshot before/after diff</span>
          </label>
          
          {/* Snapshot Diff Display (Phase 3) */}
          {snapshotDiff && showSnapshotDiff && (
            <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-200 dark:border-slate-600">
              <div className="font-semibold text-gray-700 dark:text-gray-200 mb-2">Snapshot Diff:</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="font-semibold text-red-600 dark:text-red-400 mb-1">Before</div>
                  <div className="text-xs space-y-1">
                    <div>Lines: {snapshotDiff.before?.lines?.length || 0}</div>
                    <div>Caret Map: {snapshotDiff.before?.caret_map?.length || 0}</div>
                    <div>Height: {snapshotDiff.before?.total_height_fp ? (snapshotDiff.before.total_height_fp / 64).toFixed(1) : 'N/A'}px</div>
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-green-600 dark:text-green-400 mb-1">After</div>
                  <div className="text-xs space-y-1">
                    <div>Lines: {snapshotDiff.after?.lines?.length || 0}</div>
                    <div>Caret Map: {snapshotDiff.after?.caret_map?.length || 0}</div>
                    <div>Height: {snapshotDiff.after?.total_height_fp ? (snapshotDiff.after.total_height_fp / 64).toFixed(1) : 'N/A'}px</div>
                  </div>
                </div>
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-lex-accent text-xs">View full diff</summary>
                <pre className="mt-1 text-xs overflow-auto max-h-40 text-gray-600 dark:text-gray-400 font-mono">
                  {JSON.stringify(snapshotDiff, null, 2)}
                </pre>
              </details>
            </div>
          )}
          
          <label className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              checked={highlightAffectedParagraphs} 
              onChange={(e) => setHighlightAffectedParagraphs(e.target.checked)} 
            />
            <span>Highlight affected paragraphs on canvas</span>
          </label>
        </div>
        
        {/* Test Results Display */}
        <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-800 rounded text-xs font-mono">
          <div className="text-gray-700 dark:text-gray-300 mb-1">
            Last test: {lastTestResult?.timestamp ? new Date(lastTestResult.timestamp).toLocaleTimeString() : 'None'}
          </div>
          {lastTestResult && (
            <>
              <div className={lastTestResult.passed ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-red-600 dark:text-red-400 font-semibold'}>
                {lastTestResult.passed ? '✅ PASS' : '❌ FAIL'}: {lastTestResult.message}
              </div>
              {lastTestResult.data && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-lex-accent">View details</summary>
                  <pre className="mt-1 text-xs overflow-auto max-h-40 text-gray-600 dark:text-gray-400">
                    {JSON.stringify(lastTestResult.data, null, 2)}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MarginField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <input
        type="number"
        step="0.1"
        min="0"
        max="5"
        className="w-full h-8 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-sm text-gray-900 dark:text-white"
        value={Number.isFinite(value) ? value : 0}
        onChange={onChange}
      />
    </div>
  );
}

function clamp(n: number, min = 0, max = 5) {
  if (Number.isNaN(n)) return 0;
  return Math.min(Math.max(n, min), max);
}